import { Form, SelectField, SubmitButton } from '@/components';
import {
  ProjectDetailUrlTypeLabelModifier,
  ReportLocalGovernmentFundReportTypeLabelModifier,
  ReportLocalGovernmentFundsFilterSchema,
  projectDetailUrlTypeEnum,
  type reportFileType,
  reportLocalGovernmentFundReportType,
} from '@/features/mold/models';
import { useGenerateLocalGovernmentFundsReport } from '@/features/project/report/_hooks/report';
import type { useFiscalYearOptions } from '@/features/search/_hooks/budget-types';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { useOrganizations } from '@/features/tenant-manager/organization/_hooks/organization';
import { useForm } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof ReportLocalGovernmentFundsFilterSchema>;
type InitialFormValues = Pick<
  FormValues,
  'fiscalYear' | 'reportType' | 'projectDetailUrlType'
> &
  Partial<
    Omit<FormValues, 'fiscalYear' | 'reportType' | 'projectDetailUrlType'>
  >;

type Props = {
  fileType: z.infer<typeof reportFileType>;
  fiscalYearOptions: ReturnType<typeof useFiscalYearOptions>;
};

export const ReportLocalGovernmentFundForm: FC<Props> = ({
  fileType,
  fiscalYearOptions,
}) => {
  const { t } = useTranslation();
  const currentFiscalYear = useCurrentFiscalYear();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting, isValid, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: {
      fiscalYear: currentFiscalYear.data ?? currentFiscalYear.basicFiscalYear,
      reportType: 'table-A',
      projectDetailUrlType: 'public',
    } satisfies InitialFormValues,
    resolver: zodResolver(ReportLocalGovernmentFundsFilterSchema),
  });
  const { openMessage } = useMessage();
  const { mutateAsync } = useGenerateLocalGovernmentFundsReport();

  const selectedOrganizationIsAll = watch('organizationIds')?.includes('all');
  const fiscalYear = watch('fiscalYear');

  const { data: organizationOptions, isPending: isOrganizationOptionsLoading } =
    useOrganizations({
      params: {
        level: 1,
        showMigrated: true,
        referenceFiscalYear: fiscalYear.toString(),
      },
      select: (data) =>
        data.map((item) => {
          return {
            label: item.name,
            value: item.id,
            disabled: selectedOrganizationIsAll,
          };
        }),
      enabled: !!fiscalYear,
    });

  const reportTypeOptions = reportLocalGovernmentFundReportType.options.map(
    (value) => ({
      value,
      label: ReportLocalGovernmentFundReportTypeLabelModifier[value],
    }),
  );

  const projectDetailUrlOptions = projectDetailUrlTypeEnum.options.map(
    (value) => ({
      value,
      label: ProjectDetailUrlTypeLabelModifier[value],
    }),
  );

  const onSubmit = async (values: FormValues) => {
    try {
      const isAllOrganization = values.organizationIds.includes('all');
      const paramsOrganizationIds = isAllOrganization
        ? []
        : values.organizationIds;
      await mutateAsync({
        ...values,
        organizationIds: paramsOrganizationIds,
        isAllOrganization,
        fileType,
      });
      await openMessage(
        'success',
        t('export-file-requested', {
          target: t('report-type-local-government-fund'),
        }),
      );
    } catch {
      await openMessage(
        'error',
        t('error-message-file-export-failed', {
          fileName: t('report-type-local-government-fund'),
        }),
      );
    }
  };

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
      style={{ maxWidth: 480 }}
    >
      <SelectField
        name="fiscalYear"
        label={t('fiscal-year')}
        control={control}
        options={fiscalYearOptions.data}
        errors={errors}
        allowClear={false}
        required
      />
      <SelectField
        name="organizationIds"
        label={t('organization-in-charge')}
        options={[
          {
            label: t('all-organizations'),
            value: 'all',
          },
          ...(organizationOptions ?? []),
        ]}
        onChange={(value) => {
          setValue('organizationIds', value.includes('all') ? ['all'] : value);
        }}
        shouldCoexistOnChange
        mode="multiple"
        control={control}
        errors={errors}
        allowClear={false}
        required
        loading={isOrganizationOptionsLoading}
      />
      <SelectField
        name="reportType"
        label={t('report-local-government-fund-type')}
        control={control}
        options={reportTypeOptions}
        errors={errors}
        allowClear={false}
        required
      />
      <SelectField
        name="projectDetailUrlType"
        label={t('project-detail-url-type')}
        hint={t('hint-project-detail-url-type')}
        control={control}
        options={projectDetailUrlOptions}
        errors={errors}
        allowClear={false}
        required
      />

      <SubmitButton
        text={t('export')}
        loading={isSubmitting}
        disabled={!isValid}
      />
    </Form>
  );
};
